/**
 * Credential-lookup error-state map (SIM-2 / ACS-4126).
 *
 * Maps a Backend Auth `authenticationMethods` (OI-4) error code — from the
 * `{ errors: [{ errorCode, errorMessage }] }` envelope — to how the
 * CredentialEntryScreen should present it. Source of truth: the error table in
 * artifacts/03-architecture/target-state/api-contracts/auth-service-apis.md §1
 * and the Figma "5. Errors" board (node 1307-109896).
 *
 * Scope: credential-lookup step ONLY. OTP-step error codes are SIM-4.
 *
 * `kind` values:
 *  - 'generic'              — inline generic invalid-format message (AC-018).
 *  - 'notFound'             — ACS-4362 (VQA, Figma 1308-116056): typical input-field
 *                             error BELOW the field (backend `errorMessage` + locale
 *                             fallback), NO inline link — Create Account is the bottom
 *                             button. BR-020: identical for phone and email so we never
 *                             leak which was read.
 *  - 'reactivate'           — ACS-4362 (VQA): closed/deactivated account
 *                             (ERR_ACCOUNT_CLOSED). The screen shows the reactivate
 *                             message ABOVE the field as plain red text (Figma
 *                             753-95460, no link/chat CTA): the backend `errorMessage`
 *                             (live copy, e.g. "...Or Chat with our agents.") with the
 *                             `reactivateAccount` locale key as the client fallback.
 *  - 'locked'               — ACS-4362 (VQA): security-locked account
 *                             (ERR_ACCOUNT_SYS_LOGIN). The screen shows a red message
 *                             ABOVE the field with an inline "Reset your Password"
 *                             LINK to /reset-password (Figma 1232-1309428). Client
 *                             locale copy is used (the link can't be applied to the
 *                             raw backend string), so the backend `errorMessage` is
 *                             not rendered verbatim for this state.
 *  - 'modalStoreRegistered' — ERR_400004: store-registered user with incomplete
 *                             registration. Handled specially by the screen —
 *                             open the "Check Your Email" modal ONLY when the
 *                             entered credential was an email; otherwise fall
 *                             back to the generic inline error.
 */
const CREDENTIAL_LOOKUP_ERROR_MAP = {
    ERR_PHONE_INVALID_FORMAT: { kind: 'generic' },
    ERR_PHONE_NOT_FOUND: { kind: 'notFound' },
    ERR_EMAIL_NOT_FOUND: { kind: 'notFound' },
    ERR_ACCOUNT_CLOSED: { kind: 'reactivate' },
    ERR_ACCOUNT_SYS_LOGIN: { kind: 'locked' },
    ERR_400004: { kind: 'modalStoreRegistered' }
};

// Any unrecognized code (or a code-less 5xx) falls back to the generic inline
// error so the user is never stuck without feedback.
const DEFAULT_LOOKUP_ERROR = { kind: 'generic' };

/**
 * @param {string} [errorCode]
 * @returns {{ kind: 'generic'|'notFound'|'reactivate'|'locked'|'modalStoreRegistered' }}
 */
export function getCredentialLookupError(errorCode) {
    return CREDENTIAL_LOOKUP_ERROR_MAP[errorCode] || DEFAULT_LOOKUP_ERROR;
}

export {
    CREDENTIAL_LOOKUP_ERROR_MAP, DEFAULT_LOOKUP_ERROR
};

export default getCredentialLookupError;
